// Legal documents — edit text here; pages render automatically at
// /privacy, /terms and /disclaimer. Each document is a list of
// { heading, paragraphs[] } sections.

export const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: September 2026",
    intro:
      "InSight Metabolics (“the Practice”, “we”, “us”) is a physician-led virtual metabolic and lifestyle medicine practice. This Privacy Policy explains how we collect, use, store and protect your personal information in accordance with the laws of India, including the Digital Personal Data Protection Act, 2023 (“DPDP Act”), the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 (“SPDI Rules”), and the Telemedicine Practice Guidelines, 2020.",
    sections: [
      {
        heading: "1. Who is responsible for your data",
        paragraphs: [
          "For the purposes of the DPDP Act, the Practice is the Data Fiduciary for the personal data you share with us. You may contact us about any matter in this policy at rohanearth@gmail.com.",
        ],
      },
      {
        heading: "2. Information we collect",
        paragraphs: [
          "When you complete the InSight Health Profile or request a consultation, we collect the information you choose to provide: your name, age, gender, contact details (mobile/WhatsApp number and email address), country and time zone, your health concerns and goals, lifestyle information (sleep, exercise, stress, prior approaches), and your preferred consultation time.",
          "Medical reports and laboratory investigations are reviewed only when you choose to share them with us in the course of your care.",
          "We do not use advertising trackers, and we do not collect payment card details on this website.",
        ],
      },
      {
        heading: "3. Why we collect it (purpose and consent)",
        paragraphs: [
          "We use your information only to: prepare for and conduct your consultations; contact you regarding appointments and next steps; maintain clinical records as required by applicable law and medical regulations; and, where you have asked, send you reminders about confirmed consultations.",
          "Health information is treated as sensitive personal data under the SPDI Rules and is collected with your explicit consent, which you provide before submitting the Health Profile. You may withdraw consent at any time by writing to us; withdrawal may limit our ability to provide services to you.",
        ],
      },
      {
        heading: "4. How your information is stored and protected",
        paragraphs: [
          "Your information is stored on access-controlled systems with encryption in transit. Access is restricted to the physician and essential administrative support bound by confidentiality. We retain personal data only for as long as it is needed for the purposes above or as required by applicable medical record-keeping regulations, after which it is securely deleted.",
        ],
      },
      {
        heading: "5. Who we share it with",
        paragraphs: [
          "We do not sell, rent or trade your personal information. It is shared only with service providers essential to operating the practice (secure hosting and transactional email delivery), each under appropriate confidentiality obligations, or where disclosure is required by law, court order or a regulatory authority.",
        ],
      },
      {
        heading: "6. Telemedicine consultations",
        paragraphs: [
          "Virtual consultations are conducted in accordance with the Telemedicine Practice Guidelines, 2020 issued under the Indian Medical Council (Professional Conduct, Etiquette and Ethics) framework. Consultation records are maintained as required by these guidelines.",
        ],
      },
      {
        heading: "7. Your rights under the DPDP Act",
        paragraphs: [
          "You have the right to: access a summary of your personal data and how it is being used; request correction or updating of inaccurate data; request erasure of your personal data (subject to legal record-keeping obligations); withdraw consent; nominate a person to exercise these rights in the event of death or incapacity; and raise a grievance.",
          "To exercise any of these rights, write to rohanearth@gmail.com. We will respond within the timelines prescribed by law.",
        ],
      },
      {
        heading: "8. Grievance officer",
        paragraphs: [
          "Grievance Officer: Dr Rohan Dikshit, InSight Metabolics. Email: rohanearth@gmail.com. Registered practice address: [to be added].",
        ],
      },
      {
        heading: "9. Children",
        paragraphs: [
          "Our services are intended for adults. Persons under 18 may use the services only through a parent or lawful guardian, whose consent is required before any health information is shared.",
        ],
      },
      {
        heading: "10. Changes to this policy",
        paragraphs: [
          "We may update this policy from time to time. The current version will always be available on this page, with the date of the latest revision shown above.",
        ],
      },
      {
        heading: "11. Governing law",
        paragraphs: [
          "This policy is governed by the laws of India.",
        ],
      },
    ],
  },

  terms: {
    title: "Terms of Use",
    updated: "Last updated: September 2026",
    intro:
      "These Terms of Use govern your use of the InSight Metabolics website and the services offered through it. By using this website, submitting a Health Profile or requesting a consultation, you agree to these terms.",
    sections: [
      {
        heading: "1. Nature of the service",
        paragraphs: [
          "InSight Metabolics is a physician-led virtual metabolic and lifestyle medicine practice. Content on this website — including The InSight Journal — is provided for general education and information only. It is not medical advice, diagnosis or treatment, and using this website does not by itself create a doctor–patient relationship. Such a relationship is established only through a direct consultation.",
        ],
      },
      {
        heading: "2. Not for emergencies",
        paragraphs: [
          "This website and its services are not intended for medical emergencies. If you believe you are experiencing a medical emergency, contact your local emergency services or visit the nearest hospital immediately.",
        ],
      },
      {
        heading: "3. Consultation requests and scheduling",
        paragraphs: [
          "Submitting a Health Profile or selecting a consultation slot constitutes a request, not a confirmed appointment. Appointments are confirmed by the practice team. We may decline or reschedule requests where clinically or logistically appropriate. If you need to cancel or reschedule a confirmed consultation, please inform us as early as possible.",
          "Consultation availability, prescriptions and investigations through virtual care are subject to the Telemedicine Practice Guidelines, 2020 and other applicable laws, and may vary depending on the patient's location and clinical circumstances.",
        ],
      },
      {
        heading: "4. Your responsibilities",
        paragraphs: [
          "You agree to provide accurate and complete information in your Health Profile and consultations, to inform us of significant changes in your health, and to follow the advice of your treating physicians. Do not stop or alter prescribed medication without appropriate medical supervision.",
        ],
      },
      {
        heading: "5. Fees",
        paragraphs: [
          "Consultation fees, where applicable, are communicated at the time of booking confirmation. [Fee schedule to be added.]",
        ],
      },
      {
        heading: "6. Intellectual property",
        paragraphs: [
          "All content on this website — text, design, logo and graphics — belongs to InSight Metabolics and may not be reproduced or used commercially without written permission.",
        ],
      },
      {
        heading: "7. Limitation of liability",
        paragraphs: [
          "To the extent permitted by law, the Practice is not liable for decisions made in reliance on general website content, for interruptions to the website, or for outcomes that depend on factors beyond reasonable clinical control. Individual results of any medical or lifestyle intervention vary.",
        ],
      },
      {
        heading: "8. Changes to these terms",
        paragraphs: [
          "We may revise these terms from time to time. The current version will always be available on this page.",
        ],
      },
      {
        heading: "9. Governing law and jurisdiction",
        paragraphs: [
          "These terms are governed by the laws of India, and any disputes are subject to the jurisdiction of the courts at the Practice's principal place of practice in India.",
        ],
      },
    ],
  },

  disclaimer: {
    title: "Medical Disclaimer",
    updated: "Last updated: September 2026",
    intro:
      "Please read this disclaimer carefully before using the InSight Metabolics website or acting on any of its content.",
    sections: [
      {
        heading: "1. Educational content only",
        paragraphs: [
          "All content on this website — including articles in The InSight Journal, descriptions of care pathways and answers to frequently asked questions — is provided for general education. It does not constitute medical advice, diagnosis or treatment, and must not be used as a substitute for consultation with a qualified physician.",
        ],
      },
      {
        heading: "2. No doctor–patient relationship through the website",
        paragraphs: [
          "Browsing this website, reading its content or submitting a Health Profile does not create a doctor–patient relationship. Such a relationship begins only with a direct consultation and mutual agreement to proceed with care.",
        ],
      },
      {
        heading: "3. Emergencies",
        paragraphs: [
          "Do not use this website for medical emergencies. If you experience chest pain, severe breathlessness, loss of consciousness, severe allergic reaction or any other emergency, contact your local emergency services or the nearest hospital immediately.",
        ],
      },
      {
        heading: "4. Individual results vary",
        paragraphs: [
          "Responses to metabolic, nutritional and lifestyle interventions differ from person to person. No outcome is promised or guaranteed. Recommendations are personalised according to medical history, examination, investigations and clinical needs, and are made only within a consultation.",
        ],
      },
      {
        heading: "5. Complementary to conventional care",
        paragraphs: [
          "Our approach is complementary to appropriate conventional medical care. We do not recommend stopping, reducing or replacing prescribed medication without appropriate medical supervision. Please keep your treating physicians informed.",
        ],
      },
      {
        heading: "6. Virtual care limitations",
        paragraphs: [
          "Virtual consultations are conducted in accordance with the Telemedicine Practice Guidelines, 2020. Certain conditions require in-person examination or investigations, and we will advise you accordingly. Availability of consultation, diagnosis, prescriptions and investigations may vary depending on the patient's location and applicable regulations.",
        ],
      },
    ],
  },
};
